'use client'

import Image from 'next/image'
import Link from 'next/link'

import { decodeHTML } from 'entities'

import clsx from 'clsx'

import useDateTime from '@hooks/client/useDateTime'
import { getSalaryFromJob, jobUrlBuilder } from '@lib/helpers/client'
import isSalaryVisible from '@lib/helpers/cross-env/is-salary-visible'

import companyPlaceholderIcon from '@images/searchresults/company.png'

import AiEstimatedLabel from './ai-estimated-label'
import { Benefits } from './job-facts'

import type { AnchorHTMLAttributes, JSX } from 'react'
import type { JobCardItem, TinyJobItem } from 'src/types/job-item'

/** Cuts at the last space before `max` (a hard cut when there is none) and appends an ellipsis. */
export function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text

  const cut = text.slice(0, max)
  const space = cut.lastIndexOf(' ')

  return `${(space > 0 ? cut.slice(0, space) : cut).trimEnd()}…`
}

type JobCardProps = {
  job: TinyJobItem | JobCardItem
  variant?: 'full' | 'compact'
} & AnchorHTMLAttributes<HTMLAnchorElement>

/**
 * A search-result card in the marketing design system. The whole card is one link (`prefetch={false}`,
 * like the legacy card, since a results page holds dozens), so nothing inside may be focusable; the
 * title stays an `<h2>`. Extra props land on the `<a>` so pages can attach e2e hooks. `full` adds the
 * description and benefits, `compact` is the header and facts only.
 */
export default function JobCard({ job, variant = 'full', className, ...rest }: JobCardProps): JSX.Element {
  const dateTime = useDateTime()
  const salary = getSalaryFromJob(job)
  const full = variant === 'full'

  return (
    <Link
      {...rest}
      href={jobUrlBuilder(job, 'us')}
      prefetch={false}
      className={clsx(
        'flex flex-col gap-3 rounded-lg border p-4 font-sans text-ink no-underline',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        job.premium ? 'border-accent bg-surface' : 'border-border-strong bg-surface-raised',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Image
          src={job.companyJobLogoURL || job.featuredImageUrl || companyPlaceholderIcon}
          alt=""
          width={40}
          height={40}
          className="size-10 rounded-sm object-contain"
        />
        <div className="min-w-0">
          <h2 className="text-base font-medium">{job.title}</h2>
          <p className="text-sm text-ink-secondary">{job.companyName}</p>
        </div>
      </div>
      {isSalaryVisible(job.companyName) && (
        <p className="flex flex-wrap items-center gap-2 text-base">
          {salary}
          {salary !== 'Competitive' && job.salary?.isEstimated && <AiEstimatedLabel mode="inline" />}
        </p>
      )}
      {full && 'jobDescription' in job && (
        <p className="text-base text-ink-secondary">{truncateAtWord(decodeHTML(job.jobDescription), 120)}</p>
      )}
      {full && 'benefits' in job && job.benefits && job.benefits.length > 0 && <Benefits benefits={job.benefits} />}
      <p className="flex justify-between text-sm text-ink-secondary">
        <span>{job.address?.city}</span>
        <span>{dateTime(job.publishDateTime || 0).fromNow()}</span>
      </p>
    </Link>
  )
}
